import Debug from 'debug';
import * as slip from '../pc-nrf-dfu-js/src/util/slip';
import { DfuError, ErrorCode } from '../pc-nrf-dfu-js/src/DfuError';
import DfuTransportPrn from '../pc-nrf-dfu-js/src/DfuTransportPrn';

const debug = Debug('dfu:hid');

const LPC_REPORT_ID = 0x2
const LPC_RESP_REPORT_ID = 0x2
const NRF_REPORT_ID = 0x4
const NRF_RESP_REPORT_ID = 0x5

/**
 * USB-HID DFU transport. Supports USD-HID DFU for devices connected
 * through a USB-HID to serial bridge.
 *
 * Will encode actual requests with SLIP
 */
export default class DfuTransportHid extends DfuTransportPrn {
    constructor(hidDev, packetReceiveNotification = 16) {
        super(packetReceiveNotification);
        this.dev = hidDev;
        this.devIsOpen = false;
    }

    writeNoSlip(reportId, bytes) {
        // Cast the Uint8Array info a Buffer, so it works on nodejs v6
        bytes = Buffer.from(bytes);

        // HID reports need to be 64 bytes, so we:
        // - add the reportId as the first byte
        // - add the length as second byte
        // - pad the payload with 0x00
        bytes = Buffer.concat([Buffer.from([reportId, bytes.length]), bytes]);
        bytes = Buffer.concat([bytes, Buffer.alloc(64 - bytes.length)]);

        return this.open().then(() => (
            new Promise((res, rej) => {
                let written = 0;
                if (this.dev.sendReport) {
                    // WebHID specific:
                    written = this.dev.sendReport(bytes[0], bytes.subarray(1));
                } else {
                    // Node-HID specific:
                    written = this.dev.write(bytes);
                }
                debug(' send --> [', bytes[0], '] ', bytes.subarray(1));
                if (written) {
                    res();
                } else {
                    rej();
                }
            })
        ));
    }

    // Given a command (including opcode), perform SLIP encoding and send it
    // through the wire.
    // This ensures that the serial port is open by calling this.open() - the first
    // call to writeCommand will actually open the port.
    writeCommand(bytes) {
        let encoded = slip.encode(bytes);

        // Strip the heading 0xC0 character, as to avoid a bug in the nRF SDK implementation
        // of the SLIP encoding/decoding protocol
        encoded = encoded.subarray(1);

        return this.writeNoSlip(NRF_REPORT_ID, encoded);
    }

    // Given some payload bytes, pack them into a 0x08 command.
    // The length of the bytes is guaranteed to be under this.mtu thanks
    // to the DfuTransportPrn functionality.
    writeData(bytes) {
        const commandBytes = new Uint8Array(bytes.length + 1);
        commandBytes.set([0x08], 0); // "Write" opcode
        commandBytes.set(bytes, 1);
        return this.writeCommand(commandBytes);
    }

    // Opens the HID device, sets up the event handlers and logging.
    // Returns a Promise when opening is done.
    open() {
        if (this.dev && this.dev.opened) {
            return Promise.resolve();
        }

        return new Promise(async (res, rej) => {
            // WebHID provides an open() method, NodeHID does not.
            if (this.dev.open) {
                try {
                    await this.dev.open();
                } catch (e) {
                    console.log("Couldn't open WebHID device: " + e);
                    return rej(e);
                }
            }

            debug('Initializing SLIP decoder.');
            // Start listening for data, and pipe it all through a SLIP decoder.
            // This code will listen to events from the SLIP decoder instead
            // of from the hid device itself.
            this.slipDecoder = new slip.Decoder({
                onMessage: this.onData.bind(this),
            });

            // Bind RX
            if (this.dev.on) {
                // NODE-HID specific
                this.dev.on('data', this.onRawDataNode.bind(this));
            } else {
                this.dev.addEventListener("inputreport", (event) => {
                    const { data, device, reportId } = event;

                    var bytes = Buffer.from(data.buffer);

                    // Handle only the ULX device and a specific report ID.
                    // if (device.productId !== 0x2007 && reportId !== 0x3f) return;

                    // HID report contains 64 bytes, but the first byte is the report ID.
                    // The second byte is the actual length of the data.
                    // The rest is padded with 0x00.
                    const length = Math.min(62, bytes[0]);
                    const payload = bytes.subarray(1, length + 1);

                    this.onRawData(reportId, payload);
                });
            }

            return res();
        });
    }

    // Callback when raw (yet undecoded by SLIP) data is being read from the HID device instance.
    // Called only internally.
    onRawData(reportId, data) {
        debug('[webHID] recv <-- ', data);
        // verify data came from nRF, by checking reportID
        if (reportId == NRF_RESP_REPORT_ID) {
            return this.slipDecoder.decode(data);
        } else {
            return;
        }
    }

    onRawDataNode(data) {
        debug('[nodeHID] recv <-- ', data);
        // verify data came from nRF, by checking reportID
        let reportId = data[0];
        if (reportId == NRF_RESP_REPORT_ID) {
            return this.slipDecoder.decode(data.subarray(1));
        } else {
            return;
        }
    }

    // Initializes DFU procedure: sets the PRN and requests the MTU.
    // The HID device is implicitly opened during the first call to writeCommand().
    // Returns a Promise when initialization is done.
    ready() {
        if (this.readyPromise) {
            return this.readyPromise;
        }

        this.readyPromise = this.writeCommand(new Uint8Array([
            0x02, // "Set PRN" opcode
            this.prn & 0xFF, // PRN LSB
            (this.prn >> 8) & 0xFF, // PRN MSB
        ]))
            .then(this.read.bind(this))
            .then(this.assertPacket(0x02, 0))
            // Request MTU
            .then(() => this.writeCommand(new Uint8Array([
                0x07, // "Request serial MTU" opcode
            ])))
            .then(this.read.bind(this))
            .then(this.assertPacket(0x07, 2))
            .then(bytes => {
                const mtu = (bytes[1] * 256) + bytes[0];

                // Is the USB HID MTU the lower bound?
                if (mtu >= this.mtu) {
                    debug(`Keeping USB HID MTU as lower bound: ${this.mtu}. (Serial link reported: ${mtu})`);
                    return;
                }

                // Convert wire MTU into max size of data before SLIP encoding:
                // This takes into account:
                // - SLIP encoding ( /2 )
                // - SLIP end separator ( -1 )
                // - Serial DFU write command ( -1 )
                this.mtu = Math.floor((mtu / 2) - 2);

                // Round down to multiples of 4.
                // This is done to avoid errors while writing to flash memory:
                // writing an unaligned number of bytes will result in an
                // error in most chips.
                this.mtu -= this.mtu % 4;

                debug(`USB HID MTU: ${mtu}; un-encoded data max size: ${this.mtu}`);
            });

        return this.readyPromise;
    }

    // Returns a Promise to the version of the DFU protocol that the target implements, as
    // a single integer between 0 and 255.
    // Only bootloaders from 2018 (SDK >= v15) for development boards implement this command.
    getProtocolVersion() {
        debug('GetProtocolVersion');

        return this.writeCommand(new Uint8Array([
            0x00, // "Version Command" opcode
        ]))
            .then(this.read.bind(this))
            .then(this.assertPacket(0x00, 1))
            .then(bytes => bytes[0])
            .then(protocolVersion => {
                debug('ProtocolVersion: ', protocolVersion);
                return protocolVersion;
            });
    }

    // Returns a Promise to the version of the DFU protocol that the target implements, as
    // an object with descriptive property names.
    // Only bootloaders from 2018 (SDK >= v15) for development boards implement this command.
    getHardwareVersion() {
        debug('GetHardwareVersionn');

        return this.writeCommand(new Uint8Array([
            0x0A, // "Version Command" opcode
        ]))
            .then(this.read.bind(this))
            .then(this.assertPacket(0x0A, 20))
            .then(bytes => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset than the Uint8Array for the packet payload
                const dataView = new DataView(bytes.buffer, bytes.byteOffset);
                return {
                    part: dataView.getInt32(0, true),
                    variant: dataView.getInt32(4, true),
                    memory: {
                        romSize: dataView.getInt32(8, true),
                        ramSize: dataView.getInt32(12, true),
                        romPageSize: dataView.getInt32(16, true),
                    },
                };
            })
            .then(hwVersion => {
                debug('HardwareVersion part: ', hwVersion.part.toString(16));
                debug('HardwareVersion variant: ', hwVersion.variant.toString(16));
                debug('HardwareVersion ROM: ', hwVersion.memory.romSize);
                debug('HardwareVersion RAM: ', hwVersion.memory.ramSize);
                debug('HardwareVersion ROM page size: ', hwVersion.memory.romPageSize);

                return hwVersion;
            });
    }

    // Given an image number (0-indexed), returns a Promise to a plain object describing
    // that firmware image, or boolean false if there is no image at that index.
    // Only bootloaders from 2018 (SDK >= v15) for development boards implement this command.
    getFirmwareVersion(imageCount = 0) {
        debug('GetFirmwareVersion');

        return this.writeCommand(new Uint8Array([
            0x0B, // "Version Command" opcode
            `0x${imageCount.toString(16)}`,
        ]))
            .then(this.read.bind(this))
            .then(this.assertPacket(0x0B, 13))
            .then(bytes => {
                // Decode little-endian fields, by using a DataView with the
                // same buffer *and* offset than the Uint8Array for the packet payload
                const dataView = new DataView(bytes.buffer, bytes.byteOffset);
                let imgType = dataView.getUint8(0, true);

                switch (imgType) {
                    case 0xFF:
                        // Meaning "no image at this index"
                        return false;
                    case 0:
                        imgType = 'SoftDevice';
                        break;
                    case 1:
                        imgType = 'Application';
                        break;
                    case 2:
                        imgType = 'Bootloader';
                        break;
                    default:
                        throw new DfuError(ErrorCode.ERROR_RSP_UNSUPPORTED_OBJECT_TYPE);
                }

                return {
                    version: dataView.getUint32(1, true),
                    addr: dataView.getUint32(5, true),
                    length: dataView.getUint32(9, true),
                    imageType: imgType,
                };
            })
            .then(fwVersion => {
                if (fwVersion) {
                    debug(`FirmwareVersion: image ${imageCount} is ${fwVersion.imageType} @0x${fwVersion.addr.toString(16)}+0x${fwVersion.length}`);
                } else {
                    debug('FirmwareVersion: no more images.');
                }

                return fwVersion;
            });
    }

    // Returns an array containing information about all available firmware images, by
    // sending several GetFirmwareVersion commands.
    getAllFirmwareVersions(index = 0, accum = []) {
        return this.getFirmwareVersion(index)
            .then(imageInfo => {
                if (imageInfo) {
                    accum.push(imageInfo);
                    return this.getAllFirmwareVersions(index + 1, accum);
                }
                return accum;
            });
    }
}
